import { computed } from 'vue';

import { getSubBus } from '@/platform/host-sdk/host-bridge';
import {
  ACCOUNT_INFO_KEY,
  STALE_TIME,
  accountInfoQueryOptions,
  fetchAccountInfo,
} from '@/platform/identity/account-info-query';
import { useUserStore } from '@/platform/identity/store';
import { HOST_EVENTS, INVALIDATE_RESOURCE, emitMessage } from '@celerdata/host-bridge-contract';
import { useQuery, type QueryClient } from '@tanstack/vue-query';
import { storeToRefs } from 'pinia';

import type { AccountInfoResponse } from './api';

// Host-owned account info. Server state — never mirrored into the Pinia
// identity store. Sub-side mutations (legacy account/getAccountInfo action)
// reach this query via SUB_EVENTS.INVALIDATE { resource: 'account-info' }
// — emitted by legacy's sub-invalidate-plugin (subscribeAction map) and
// handled by INVALIDATE_HANDLERS in sub-broadcaster.ts.
export { ACCOUNT_INFO_KEY } from '@/platform/identity/account-info-query';

// Invalidate host's local query AND signal sub to refresh its own Vuex.
// Use when host learned the data is stale (WS push, payment edit).
// Auth-boundary resets (logout / timeout / SSO re-auth) rely on a fresh
// document; plain password login fetches identity before leaving /login,
// so neither path emits this manually.
export function invalidateAccountInfo(qc: QueryClient): void {
  void qc.invalidateQueries({ queryKey: ACCOUNT_INFO_KEY });
  emitMessage(getSubBus(), HOST_EVENTS.INVALIDATE, {
    resource: INVALIDATE_RESOURCE.ACCOUNT_INFO,
  });
}

// Force a fresh account-info fetch for a specific account, then signal sub.
// Unlike invalidateAccountInfo, this runs the queryFn even when no component
// is observing the query (chrome-less routes: /401, /404, transient auth
// pages) — invalidateQueries only refetches active observers, leaving an
// inactive query merely stale. The ACCOUNT_UNSUBSCRIBE (state === -2) forced
// sign-out lives in the queryFn, so it must actually execute on the
// GlobalEventTypeAccountStatusChanged push regardless of the current route.
// staleTime 0 defeats the 5-min cache so a genuine WS-driven status change
// always hits the network.
export function refreshAccountInfo(qc: QueryClient, accountID: string): void {
  // fetchQuery rejects on API failure; the axios interceptor owns the toast and
  // the forced sign-out runs inside the queryFn, so swallow the rejection here
  // to keep this fire-and-forget call from surfacing as an unhandled rejection.
  void qc.fetchQuery({ ...accountInfoQueryOptions(accountID), staleTime: 0 }).catch(() => {});
  emitMessage(getSubBus(), HOST_EVENTS.INVALIDATE, {
    resource: INVALIDATE_RESOURCE.ACCOUNT_INFO,
  });
}

export function useAccountInfoQuery() {
  const { info } = storeToRefs(useUserStore());
  return useQuery<AccountInfoResponse>({
    queryKey: computed(() => [...ACCOUNT_INFO_KEY, info.value?.accountID]),
    queryFn: () => {
      const accountID = info.value?.accountID;
      if (!accountID) throw new Error('useAccountInfoQuery: accountID required');
      return fetchAccountInfo(accountID);
    },
    enabled: computed(() => !!info.value?.accountID),
    staleTime: STALE_TIME,
    refetchOnReconnect: false,
  });
}
